import React, { createContext, useContext, useState, useEffect } from "react";

const HistoryContext = createContext();

export const useHistory = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error("useHistory must be used within HistoryProvider");
  }
  return context;
};

export const HistoryProvider = ({ children }) => {
  // Charger l'historique depuis localStorage
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem("loi25_history");
    return saved ? JSON.parse(saved) : [];
  });

  // Sauvegarder dans localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem("loi25_history", JSON.stringify(history));
  }, [history]);

  // Ajouter une entrée à l'historique
  const addHistoryEntry = (entry) => {
    const newEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...entry,
    };
    setHistory((prev) => [newEntry, ...prev]);
    return newEntry;
  };

  // Types d'actions disponibles
  const actionTypes = {
    CREATE: "create",
    UPDATE: "update",
    DELETE: "delete",
    EXPORT: "export",
    VIEW: "view",
    LOGIN: "login",
    LOGOUT: "logout",
  };

  // Types d'entités disponibles
  const entityTypes = {
    OBLIGATION: "obligation",
    EFVP: "efvp",
    INCIDENT: "incident",
    CONSENT: "consent",
    USER: "user",
    DOCUMENT: "document",
    REPORT: "report",
    SETTINGS: "settings",
  };

  // Fonction helper pour créer une entrée
  const logAction = ({
    action,
    entityType,
    entityId,
    entityName,
    description,
    details = {},
    user = null,
  }) => {
    return addHistoryEntry({
      action,
      entityType,
      entityId,
      entityName,
      description,
      details,
      user: user || getCurrentUser(),
      ipAddress: "127.0.0.1", // En production, récupérer l'IP réelle
    });
  };

  // Récupérer l'utilisateur courant (simulé pour l'instant)
  const getCurrentUser = () => {
    const saved = localStorage.getItem("loi25_current_user");
    return saved
      ? JSON.parse(saved)
      : { name: "Utilisateur", email: "user@entreprise.com" };
  };

  // Définir l'utilisateur courant
  const setCurrentUser = (user) => {
    localStorage.setItem("loi25_current_user", JSON.stringify(user));
  };

  // Filtrer l'historique
  const filterHistory = ({
    action = null,
    entityType = null,
    userId = null,
    startDate = null,
    endDate = null,
    searchTerm = null,
  }) => {
    return history.filter((entry) => {
      if (action && entry.action !== action) return false;
      if (entityType && entry.entityType !== entityType) return false;
      if (userId && entry.user?.email !== userId) return false;

      if (startDate) {
        const entryDate = new Date(entry.timestamp);
        if (entryDate < new Date(startDate)) return false;
      }

      if (endDate) {
        const entryDate = new Date(entry.timestamp);
        if (entryDate > new Date(endDate)) return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesDescription = entry.description
          ?.toLowerCase()
          .includes(term);
        const matchesEntityName = entry.entityName
          ?.toLowerCase()
          .includes(term);
        const matchesUser = entry.user?.name?.toLowerCase().includes(term);
        if (!matchesDescription && !matchesEntityName && !matchesUser)
          return false;
      }

      return true;
    });
  };

  // Obtenir les statistiques
  const getStats = () => {
    const stats = {
      total: history.length,
      byAction: {},
      byEntityType: {},
      byUser: {},
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    history.forEach((entry) => {
      // Par action
      stats.byAction[entry.action] = (stats.byAction[entry.action] || 0) + 1;

      // Par type d'entité
      stats.byEntityType[entry.entityType] =
        (stats.byEntityType[entry.entityType] || 0) + 1;

      // Par utilisateur
      const userName = entry.user?.name || "Inconnu";
      stats.byUser[userName] = (stats.byUser[userName] || 0) + 1;

      // Par période
      const entryDate = new Date(entry.timestamp);
      if (entryDate >= today) stats.today++;
      if (entryDate >= weekAgo) stats.thisWeek++;
      if (entryDate >= monthAgo) stats.thisMonth++;
    });

    return stats;
  };

  // Exporter l'historique en JSON
  const exportHistory = () => {
    const dataStr = JSON.stringify(history, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `historique_loi25_${
      new Date().toISOString().split("T")[0]
    }.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Exporter en CSV
  const exportHistoryCSV = () => {
    const headers = [
      "Date/Heure",
      "Action",
      "Type",
      "Entité",
      "Description",
      "Utilisateur",
      "Email",
      "IP",
    ];

    const rows = history.map((entry) => [
      new Date(entry.timestamp).toLocaleString("fr-FR"),
      entry.action,
      entry.entityType,
      entry.entityName || "-",
      entry.description,
      entry.user?.name || "-",
      entry.user?.email || "-",
      entry.ipAddress || "-",
    ]);

    const csv = [headers, ...rows].map((row) => row.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `historique_loi25_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Effacer l'historique (avec confirmation)
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem("loi25_history");
  };

  const value = {
    history,
    addHistoryEntry,
    logAction,
    actionTypes,
    entityTypes,
    filterHistory,
    getStats,
    exportHistory,
    exportHistoryCSV,
    clearHistory,
    setCurrentUser,
    getCurrentUser,
  };

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
};

export default HistoryContext;
